import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  Label,
  Spinner,
} from '@ng-unifi/ui';
import {
  completeMfaBootstrap,
  fetchSetupStatus,
  initSetup,
  probeUnifi,
  type ProbeResult,
  type SetupStatus,
  type SiteMode,
} from '../lib/setupStatus.js';
import { ApiError } from '../lib/api.js';

type Step = 'site' | 'unifi' | 'master' | 'mfa' | 'done';

const STEP_LABEL: Record<Step, string> = {
  site: 'Site',
  unifi: 'UniFi',
  master: 'Master admin',
  mfa: 'MFA',
  done: 'Done',
};

export function SetupPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [step, setStep] = useState<Step>('site');

  const [setupToken, setSetupToken] = useState('');
  const [siteName, setSiteName] = useState('');
  const [siteMode, setSiteMode] = useState<SiteMode>('commercial_multi_tenant');
  const [unifi, setUnifi] = useState({
    base_url: '',
    api_key: '',
    tls_reject_unauthorized: true,
  });
  const [probe, setProbe] = useState<
    | { state: 'idle' }
    | { state: 'probing' }
    | {
        state: 'ok';
        controller_version: string;
        uptime_seconds: number;
        api_path: '/api/v1/developer';
        capabilities: ProbeResult['capabilities'];
      }
    | { state: 'error'; message: string }
  >({ state: 'idle' });

  const [master, setMaster] = useState({ email: '', name: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [mfa, setMfa] = useState<{
    secret: string;
    qr: string;
    bootstrap_token: string;
  } | null>(null);
  const [otp, setOtp] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  const [generatedHmac, setGeneratedHmac] = useState<string | null>(null);

  useEffect(() => {
    fetchSetupStatus()
      .then((s) => {
        setStatus(s);
        setSiteName((s.site_name === 'Access Portal' ? '' : s.site_name) ?? '');
        if (s.completed) navigate('/sign-in', { replace: true });
      })
      .catch(() =>
        setStatus({
          completed: false,
          site_name: '',
          site_mode: 'commercial_multi_tenant',
          has_master: false,
          has_unifi: false,
          webhook_callback_url: '',
          requires_setup_token: true,
          mfa_enforced: true,
        }),
      );
  }, [navigate]);

  const passwordIssue = useMemo(() => {
    if (!master.password) return null;
    if (master.password.length < 12) return 'Password must be at least 12 characters.';
    if (!/[A-Za-z]/.test(master.password)) return 'Password must include letters.';
    if (!/\d/.test(master.password)) return 'Password must include a digit.';
    if (master.password !== master.confirm) return 'Passwords do not match.';
    return null;
  }, [master.password, master.confirm]);

  if (!status) {
    return (
      <main className="grid min-h-screen place-items-center bg-surface">
        <Spinner />
      </main>
    );
  }

  async function onProbe() {
    setProbe({ state: 'probing' });
    try {
      const r = await probeUnifi(
        {
          base_url: unifi.base_url,
          api_key: unifi.api_key,
          tls_reject_unauthorized: unifi.tls_reject_unauthorized,
        },
        { setupToken: status?.requires_setup_token ? setupToken : undefined },
      );
      setProbe({
        state: 'ok',
        controller_version: r.controller_version,
        uptime_seconds: r.uptime_seconds,
        api_path: r.api_path,
        capabilities: r.capabilities,
      });
    } catch (err) {
      setProbe({ state: 'error', message: err instanceof ApiError ? err.message : 'Probe failed' });
    }
  }

  async function onSubmit() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await initSetup(
        {
          site_name: siteName.trim(),
          site_mode: siteMode,
          master: {
            email: master.email.trim(),
            name: master.name.trim(),
            password: master.password,
          },
          unifi,
        },
        { setupToken: status?.requires_setup_token ? setupToken : undefined },
      );
      setGeneratedHmac(result.webhook_hmac_secret ?? null);
      if (result.mfa) {
        setMfa({
          secret: result.mfa.secret,
          qr: result.mfa.qr_png_data_url,
          bootstrap_token: result.mfa.bootstrap_token,
        });
        setStep('mfa');
      } else {
        setStep('done');
      }
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Setup failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyMfa() {
    if (!mfa) return;
    setMfaError(null);
    try {
      await completeMfaBootstrap({ bootstrap_token: mfa.bootstrap_token, otp });
      setStep('done');
    } catch (err) {
      setMfaError(err instanceof ApiError ? err.message : 'MFA verification failed');
    }
  }

  return (
    <main className="min-h-screen bg-surface py-12">
      <div className="mx-auto w-full max-w-2xl px-4">
        <ol className="mb-6 flex items-center justify-between text-sm">
          {(['site', 'unifi', 'master', 'mfa', 'done'] as Step[])
            .filter((s) => s !== 'mfa' || status.mfa_enforced)
            .map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    step === s
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-high text-on-surface-variant'
                  }`}
                >
                  {i + 1}
                </span>
                {/* s is iterated from the STEPS typed-literal array. */}
                {/* eslint-disable-next-line security/detect-object-injection */}
                <span className="text-on-surface-variant">{STEP_LABEL[s]}</span>
              </li>
            ))}
        </ol>

        {step === 'site' && (
          <Card>
            <CardHeader>
              <h1 className="text-xl font-semibold">Welcome</h1>
              <p className="text-sm text-on-surface-variant">
                Give this Access Portal instance a name. Tenants and users will see it on sign-in
                and in the top app bar.
              </p>
            </CardHeader>
            <CardBody className="space-y-4">
              {status.requires_setup_token ? (
                <div>
                  <Label htmlFor="setup-token">Setup token</Label>
                  <Input
                    id="setup-token"
                    type="password"
                    autoComplete="off"
                    value={setupToken}
                    onChange={(e) => setSetupToken(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">
                    Issued to you by ops. Required for first-run setup; rotated after install.
                  </p>
                </div>
              ) : null}
              <div>
                <Label htmlFor="site">Site name</Label>
                <Input
                  id="site"
                  placeholder="e.g. Northgate Park"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  maxLength={120}
                />
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-on-surface">Property type</legend>
                <p className="text-xs text-on-surface-variant">
                  Pick the option that best matches the property. Switching later
                  isn't supported once you've added people, doors, and schedules,
                  so take a moment now.
                </p>
                {(
                  [
                    {
                      value: 'commercial_multi_tenant' as const,
                      label: 'Commercial building with multiple tenants',
                      desc: 'A building owner or property manager rents space to multiple businesses. Each tenant manages its own employees.',
                    },
                    {
                      value: 'single_tenant' as const,
                      label: 'Single organization (one company on the whole site)',
                      desc: 'One organization owns and operates the whole site — no separate tenants to manage.',
                    },
                    {
                      value: 'multi_family' as const,
                      label: 'Multi-family residential (apartment or condo)',
                      desc: 'A property manager handles units, residents, households, amenities, callbox/intercom, and guests.',
                    },
                  ] satisfies { value: SiteMode; label: string; desc: string }[]
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-3 rounded border border-on-surface-variant p-3 hover:border-primary"
                  >
                    <input
                      type="radio"
                      name="site-mode"
                      value={opt.value}
                      checked={siteMode === opt.value}
                      onChange={() => setSiteMode(opt.value)}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-medium text-on-surface">{opt.label}</span>
                      <span className="block text-xs text-on-surface-variant">{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </CardBody>
            <CardFooter>
              <Button
                disabled={
                  !siteName.trim() || (status.requires_setup_token && setupToken.length < 16)
                }
                onClick={() => setStep('unifi')}
              >
                Continue
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 'unifi' && (
          <Card>
            <CardHeader>
              <h1 className="text-xl font-semibold">Connect to UniFi Access</h1>
              <p className="text-sm text-on-surface-variant">
                Provide the controller URL and an API key with Access scope. The portal validates
                the connection before saving.
              </p>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <Label htmlFor="base-url">Controller base URL</Label>
                <Input
                  id="base-url"
                  placeholder="https://unifi-access.local:12445"
                  value={unifi.base_url}
                  onChange={(e) => setUnifi({ ...unifi, base_url: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="api-key">API key</Label>
                <Input
                  id="api-key"
                  type="password"
                  autoComplete="off"
                  value={unifi.api_key}
                  onChange={(e) => setUnifi({ ...unifi, api_key: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={unifi.tls_reject_unauthorized}
                  onChange={(e) =>
                    setUnifi({ ...unifi, tls_reject_unauthorized: e.target.checked })
                  }
                />
                Reject untrusted TLS certificates (uncheck only for on-prem with self-signed cert)
              </label>
              {/*
                Live-verification banner. Capability flags shown here come from
                a probe response, not from the compatibility matrix itself.
                Core rows in the matrix have pilot live evidence on documented
                firmware, while NFC/touch enrollment rows remain explicit
                deferred exceptions until dedicated live runs complete.
              */}
              <p
                role="status"
                className="rounded-md border border-tertiary-fixed-dim bg-tertiary-fixed p-2 text-xs text-on-tertiary-fixed"
                data-testid="unverified-controller-banner"
              >
                <Badge tone="warning">Unverified for your controller</Badge>{' '}
                Capability flags surfaced here reflect what the controller advertises. Core
                compatibility rows have pilot live evidence for the documented firmware, but your
                exact controller and firmware should still be verified before production. NFC,
                touch-pass, and NFC enrollment-session rows remain deferred exception flows unless
                you run the live contract workflow. See{' '}
                <code>docs/runbooks/unifi-live-verification.md</code>.
              </p>
              {probe.state === 'probing' && (
                <p className="text-sm text-on-surface-variant">Testing connection…</p>
              )}
              {probe.state === 'ok' && (
                <div className="space-y-2 text-sm">
                  <p>
                    <Badge tone="success">Connected</Badge> UniFi Access Developer API reachable at{' '}
                    <code>{probe.api_path}</code>. Controller version is not exposed by this probe.
                  </p>
                  {(() => {
                    const caps = probe.capabilities;
                    // 'unknown' is the pre-probe initial state and is
                    // intentionally not surfaced as degraded. We only flag
                    // capabilities the controller has actively answered
                    // 'unsupported' or 'missing_permission' on.
                    const degraded = Object.entries(caps).filter(
                      ([k, v]) => k !== 'api_path' && (v === 'unsupported' || v === 'missing_permission'),
                    );
                    if (degraded.length === 0) return null;
                    return (
                      <p
                        role="alert"
                        className="rounded-md border border-error bg-error-container p-2 text-xs text-on-error-container"
                        data-testid="degraded-features-banner"
                      >
                        <Badge tone="danger">Feature degraded</Badge> These portal features will be
                        limited on this controller:{' '}
                        {degraded
                          .map(([k, v]) => `${k.replace(/^supports_/, '').replace(/_/g, ' ')} (${v as string})`)
                          .join(', ')}
                        .
                      </p>
                    );
                  })()}
                  <div className="grid gap-1 text-xs text-on-surface-variant">
                    {(
                      [
                        ['Webhooks', probe.capabilities.supports_webhooks],
                        ['Visitors', probe.capabilities.supports_visitors],
                        ['Touch Passes', probe.capabilities.supports_touch_passes],
                        ['License Plates', probe.capabilities.supports_license_plates],
                        ['Door Lock Rules', probe.capabilities.supports_door_lock_rules],
                        ['Device Settings', probe.capabilities.supports_device_settings],
                        ['Access Policies', probe.capabilities.supports_access_policies],
                        ['User Groups', probe.capabilities.supports_user_groups],
                        ['System Logs', probe.capabilities.supports_system_logs],
                      ] as Array<[string, ProbeResult['capabilities'][keyof ProbeResult['capabilities']]]>
                    ).map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between">
                        <span>
                          {label}{' '}
                          <Badge tone="neutral" data-testid={`unverified-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                            Unverified
                          </Badge>
                        </span>
                        <code>{value}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {probe.state === 'error' && (
                <p role="alert" className="text-sm text-error">
                  {probe.message}
                </p>
              )}
            </CardBody>
            <CardFooter>
              <Button variant="ghost" onClick={() => setStep('site')}>
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={onProbe}
                disabled={!unifi.base_url || !unifi.api_key || probe.state === 'probing'}
              >
                Test connection
              </Button>
              <Button onClick={() => setStep('master')} disabled={probe.state !== 'ok'}>
                Continue
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 'master' && (
          <Card>
            <CardHeader>
              <h1 className="text-xl font-semibold">Master admin</h1>
              <p className="text-sm text-on-surface-variant">
                The first user. They will be able to add tenants, grant door permissions, and create
                additional admins.
                {status.mfa_enforced
                  ? ' MFA is required and will be enrolled in the next step.'
                  : null}
              </p>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <Label htmlFor="m-name">Full name</Label>
                <Input
                  id="m-name"
                  value={master.name}
                  onChange={(e) => setMaster({ ...master, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="m-email">Email</Label>
                <Input
                  id="m-email"
                  type="email"
                  autoComplete="off"
                  value={master.email}
                  onChange={(e) => setMaster({ ...master, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="m-pw">Password</Label>
                <Input
                  id="m-pw"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={master.password}
                  onChange={(e) => setMaster({ ...master, password: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="m-confirm">Confirm password</Label>
                <Input
                  id="m-confirm"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={master.confirm}
                  onChange={(e) => setMaster({ ...master, confirm: e.target.value })}
                  invalid={Boolean(passwordIssue && master.confirm.length > 0)}
                />
                {passwordIssue && master.confirm.length > 0 ? (
                  <p className="mt-1 text-sm text-error">{passwordIssue}</p>
                ) : null}
              </div>
              {submitError ? (
                <p role="alert" className="text-sm text-error">
                  {submitError}
                </p>
              ) : null}
            </CardBody>
            <CardFooter>
              <Button variant="ghost" onClick={() => setStep('unifi')}>
                Back
              </Button>
              <Button
                onClick={onSubmit}
                loading={submitting}
                disabled={
                  Boolean(passwordIssue) ||
                  !master.name.trim() ||
                  !master.email.trim() ||
                  master.password.length < 12 ||
                  master.password !== master.confirm
                }
              >
                {status.mfa_enforced ? 'Continue to MFA' : 'Finish setup'}
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 'mfa' && mfa && (
          <Card>
            <CardHeader>
              <h1 className="text-xl font-semibold">Enroll MFA for master admin</h1>
              <p className="text-sm text-on-surface-variant">
                Scan the QR with an authenticator app, then enter the 6-digit code to finish setup.
              </p>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex flex-col items-center gap-2">
                <img alt="MFA QR" src={mfa.qr} className="h-48 w-48 rounded border border-surface-container-high" />
                <p className="text-xs text-on-surface-variant">
                  Backup secret: <code>{mfa.secret}</code>
                </p>
              </div>
              <div>
                <Label htmlFor="otp">6-digit code</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              {mfaError ? (
                <p role="alert" className="text-sm text-error">
                  {mfaError}
                </p>
              ) : null}
            </CardBody>
            <CardFooter>
              <Button onClick={onVerifyMfa} disabled={otp.length !== 6}>
                Verify + finish setup
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 'done' && (
          <Card>
            <CardHeader>
              <h1 className="text-xl font-semibold">Setup complete</h1>
            </CardHeader>
            <CardBody className="space-y-4 text-sm">
              <p>
                Configure the controller to deliver webhooks to:{' '}
                <code className="rounded bg-surface-container-high px-1">
                  {status.webhook_callback_url}
                </code>
              </p>
              {generatedHmac ? (
                <div>
                  <p>
                    Store this inbound webhook HMAC secret (shown <strong>once</strong>) so your
                    receiver and portal verification use the same value:
                  </p>
                  <code className="block break-all rounded bg-surface-container-high p-2">
                    {generatedHmac}
                  </code>
                </div>
              ) : null}
              <Button onClick={() => navigate('/sign-in')}>Continue to sign-in</Button>
            </CardBody>
          </Card>
        )}
      </div>
    </main>
  );
}
